import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';
import parse from 'html-react-parser';
import { Helmet } from 'react-helmet-async';

import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  RedditShareButton,
} from 'react-share';

import { NewLoginForm } from './NewLoginDailogForm';
import { BackDropLoad } from './LoadingBackDrop';
import { Loading } from './Loading';
import { UserContext } from './userContext';
import { Protected, LoginRequired } from '../theme/primitives';
import { ShareGlyph, ShareBubble } from './ShareIcons';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

/* ─────────── Inline icons ─────────── */

const Icon = {
  Calendar: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Link: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Check: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
};

/* ═══════════════════ SingleTrend ═══════════════════ */

export const SingleTrend = () => {
  const { id } = useParams();
  const url = window.location.href;
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState(null);
  const [showLoginForm, setShowLoginForm] = useState(!user);
  const [wait, setWait] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Axios.get('/new_apis/get-single-trend', {
      params: { trendid: id },
      headers: { key },
    }).then((res) => {
      setTrendData(res.data.trenddata || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id, user]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <Loading />;

  if (!user) {
    return (
      <>
        <Helmet><title>Sign in required | Trends | ESAY</title></Helmet>
        <BackDropLoad wait={wait} />
        <NewLoginForm
          strict
          strictTitle="Sign in to view Trends"
          strictMessage="Trends are available to ESAY members. Log in to read the full article."
          open={showLoginForm}
          showload={() => setWait(true)}
          hideload={() => setWait(false)}
          closeform={() => setShowLoginForm(false)}
          openloginform={() => setShowLoginForm(true)}
        />
        <LoginRequired
          title="Sign in to read this trend"
          message="Trends are available to ESAY members. Sign in to read the full article."
          onLogin={() => setShowLoginForm(true)}
        />
      </>
    );
  }

  if (!trendData) return null;

  const showCTA = trendData.is_trend === '1' && (trendData.trend_type === '1' || trendData.trend_type === '2');
  const ctaTo = trendData.trend_type === '1'
    ? `/psdetails/${trendData.p_slug}/${trendData.p_id}`
    : `/eng-info/info-detail/${trendData.info_slug}/${trendData.info_id}`;
  const ctaLabel = trendData.trend_type === '1' ? 'View details' : 'View in Engineering Info';

  return (
    <Protected>
      <Helmet>
        <title>{trendData.blog_title} | Trends | ESAY</title>
      </Helmet>

      <BackDropLoad wait={wait} />
      <NewLoginForm
        strict
        strictTitle="Sign in to view Trends"
        strictMessage="Trends are available to ESAY members. Log in to read the full article."
        open={showLoginForm}
        showload={() => setWait(true)}
        hideload={() => setWait(false)}
        closeform={() => setShowLoginForm(false)}
        openloginform={() => setShowLoginForm(true)}
      />

      <article className="rounded-xl border border-border-subtle bg-surface-paper p-5 sm:p-7 md:p-8">
        {/* Date kicker */}
        <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
          <Icon.Calendar className="h-3 w-3 text-amber" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">{trendData.added_date}</span>
        </div>

        {/* Title */}
        <h1 className="text-[20px] sm:text-[24px] md:text-[28px] font-bold leading-[1.2] tracking-[-0.02em] text-fg-primary">
          {trendData.blog_title}
        </h1>

        {/* Amber accent line */}
        <div aria-hidden className="mt-3 h-[2px] w-10 rounded-full bg-gradient-to-r from-amber to-amber/0" />

        {/* Hero image */}
        {trendData.blog_image && (
          <div className="mt-4 overflow-hidden rounded-lg border border-border-subtle">
            <img
              src={trendData.blog_image}
              alt={trendData.blog_title}
              className="block w-full max-h-[320px] object-cover"
            />
          </div>
        )}

        {/* CTA */}
        {showCTA && (
          <div className="mt-4">
            <RouterLink
              to={ctaTo}
              className="
                group inline-flex h-9 items-center justify-center gap-1.5
                rounded-lg px-4
                text-[11.5px] font-bold uppercase tracking-[0.1em]
                bg-amber text-ink
                hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                active:scale-[0.98]
                transition-all duration-200 ease-out-soft
              "
            >
              {ctaLabel}
              <Icon.ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
            </RouterLink>
          </div>
        )}

        {/* Share row */}
        <div className="mt-6 pt-4 border-t border-border-subtle flex flex-wrap items-center gap-2">
          <span className="mr-2 text-[10px] font-bold uppercase tracking-[0.18em] text-fg-tertiary">Share</span>
          <FacebookShareButton url={url}><ShareBubble label="Facebook" size="sm"><ShareGlyph.Facebook /></ShareBubble></FacebookShareButton>
          <LinkedinShareButton url={url}><ShareBubble label="LinkedIn" size="sm"><ShareGlyph.LinkedIn /></ShareBubble></LinkedinShareButton>
          <TwitterShareButton url={url}><ShareBubble label="X" size="sm"><ShareGlyph.X /></ShareBubble></TwitterShareButton>
          <RedditShareButton url={url}><ShareBubble label="Reddit" size="sm"><ShareGlyph.Reddit /></ShareBubble></RedditShareButton>
          <WhatsappShareButton url={url}><ShareBubble label="WhatsApp" size="sm"><ShareGlyph.WhatsApp /></ShareBubble></WhatsappShareButton>
          <TelegramShareButton url={url}><ShareBubble label="Telegram" size="sm"><ShareGlyph.Telegram /></ShareBubble></TelegramShareButton>
          <EmailShareButton url={url}><ShareBubble label="Email" size="sm"><ShareGlyph.Email /></ShareBubble></EmailShareButton>

          {/* Copy link button */}
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? 'Link copied' : 'Copy link'}
            title={copied ? 'Link copied!' : 'Click to copy the URL'}
            className={`
              inline-flex h-9 w-9 items-center justify-center rounded-full
              border border-border-subtle bg-surface-elevated
              cursor-pointer
              transition-[background-color,border-color,color,transform,box-shadow] duration-200 ease-out-soft
              hover:bg-amber hover:border-amber hover:text-ink hover:-translate-y-0.5
              hover:shadow-[0_8px_18px_-6px_rgba(251,191,36,0.55)]
              ${copied ? 'text-amber' : 'text-fg-secondary'}
            `}
          >
            {copied ? <Icon.Check className="h-[16px] w-[16px]" /> : <Icon.Link className="h-[16px] w-[16px]" />}
          </button>
        </div>

        {/* Body - white card with dark text for readable HTML content */}
        <div
          className="mt-5 rounded-lg border p-4 sm:p-5"
          style={{
            backgroundColor: '#FFFFFF',
            borderColor: 'rgba(10,10,11,0.08)',
            color: '#1f1f23',
            boxShadow: '0 4px 16px -8px rgba(0,0,0,0.4)',
          }}
        >
          <BodyContent html={trendData.blog_desc || ''} />
        </div>
      </article>
    </Protected>
  );
};

/* ─────────── Body content with ink-on-white styling ─────────── */

function BodyContent({ html }) {
  return (
    <div
      className="
        text-[13.5px] leading-[1.65]
        [&_p]:mb-2.5
        [&_a]:text-amber-deep [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-amber
        [&_img]:max-w-full [&_img]:h-auto [&_img]:max-h-[260px] [&_img]:object-contain [&_img]:rounded-md [&_img]:my-2.5
        [&_h1]:mt-4 [&_h1]:mb-1.5 [&_h1]:text-[18px] [&_h1]:font-bold
        [&_h2]:mt-4 [&_h2]:mb-1.5 [&_h2]:text-[16px] [&_h2]:font-bold
        [&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-[15px] [&_h3]:font-bold
        [&_h4]:mt-3 [&_h4]:mb-1.5 [&_h4]:text-[13.5px] [&_h4]:font-bold
        [&_h1]:text-ink [&_h2]:text-ink [&_h3]:text-ink [&_h4]:text-ink
        [&_strong]:text-ink [&_strong]:font-bold
        [&_b]:text-ink [&_b]:font-bold
        [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:my-1.5
        [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:my-1.5
        [&_li]:my-0.5
        [&_blockquote]:border-l-[3px] [&_blockquote]:border-amber [&_blockquote]:pl-3 [&_blockquote]:my-2.5 [&_blockquote]:italic [&_blockquote]:text-[#444]
        [&_code]:bg-black/[0.06] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[0.875em]
        [&_table]:w-full [&_table]:my-2.5 [&_table]:border-collapse [&_table]:bg-[#FAFAFA] [&_table]:rounded-md [&_table]:overflow-hidden
        [&_th]:bg-[#F0F0EE] [&_th]:text-ink [&_th]:font-bold [&_th]:p-2 [&_th]:text-left [&_th]:border [&_th]:border-black/10
        [&_td]:p-2 [&_td]:text-left [&_td]:border [&_td]:border-black/10
      "
      style={{ color: '#1f1f23' }}
    >
      {parse(html)}
    </div>
  );
}
